import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { INVITE_TO_RQ_MODAL_STEP } from '@/components/modals/InviteToRqModal/constants'
import { InviteToRqModalStep } from '@/components/modals/InviteToRqModal/types'
import { RootState } from '@/redux/store'

/**
 * The invite-to-RQ modal, in full.
 *
 * None of it lives in the URL. It used to, which meant going back to the previous
 * page reopened the modal along with it — the history entry the modal added is
 * the same entry the back button lands on. The addresses have their own reason to
 * stay out: they're other people's, and a prefill isn't worth linking to.
 */
export interface InviteToRqModalState {
  isShowing: boolean
  step: InviteToRqModalStep
  emails: string[]
}

const INITIAL_STATE: InviteToRqModalState = {
  isShowing: false,
  step: INVITE_TO_RQ_MODAL_STEP.inviteTypeChoice,
  emails: [],
}

interface ShowPayload {
  step?: InviteToRqModalStep
  emails?: string[]
}

const inviteToRqModal = createSlice({
  name: 'inviteToRqModal',
  initialState: INITIAL_STATE,
  reducers: {
    showInviteToRqModal: (state, action: PayloadAction<ShowPayload>) => {
      state.isShowing = true
      state.step = action.payload.step ?? state.step
      // Always set, so opening the modal from somewhere else can't inherit the
      // last caller's prefill.
      state.emails = action.payload.emails ?? []
    },

    setInviteToRqModalStep: (
      state,
      action: PayloadAction<InviteToRqModalStep>,
    ) => {
      state.step = action.payload
    },

    hideInviteToRqModal: () => INITIAL_STATE,
  },
})

export const {
  showInviteToRqModal,
  setInviteToRqModalStep,
  hideInviteToRqModal,
} = inviteToRqModal.actions

export const selectInviteToRqModal = (state: RootState) => state.inviteToRqModal

export const selectIsShowingInviteToRqModal = createSelector(
  selectInviteToRqModal,
  (modal) => modal.isShowing,
)

export const selectInviteToRqModalStep = createSelector(
  selectInviteToRqModal,
  (modal) => modal.step,
)

export const selectInviteToRqModalEmails = createSelector(
  selectInviteToRqModal,
  (modal) => modal.emails,
)

export const inviteToRqModalReducer = inviteToRqModal.reducer
